import React, { Dispatch, useMemo } from 'react';
import { MenuItem, Link, Stack, Box } from '@mui/material';
import Icon from '@mdi/react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../localization/i18n';
import {
    mdiHelpCircle,
    mdiSchool,
    mdiInformation,
    mdiCounter,
    mdiFileDocumentMultiple,
    mdiHelpRhombusOutline,
    mdiSecurity,
    mdiTeamviewer,
} from '@mdi/js';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { openProjectDocumentationDialog } from '../../../redux/project/sliceProjectDocumentation';
import { labelFontSemiBold } from '../../../Theme';
import store, { IAppReduxStore } from '../../../redux/store';
import { findProjectDocuments } from '../../../util/DocUtils';
import { actionLogSupportTicket } from '../../../redux/project/sliceProjectSupportTicketCreation';

/** React app version number. */
const versionNumber = process.env?.REACT_APP_VERSION_NUMBER;

/**
 * Component that allows the user to navigate through help options.
 */
export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'header' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const { replicaId, areaName } = useParams();

    const userProfile  = useSelector((store: IAppReduxStore) => store.userProfile.data);
    const projectData = useSelector((store: IAppReduxStore) => store.projectMetadata.data);
    const selectedDocs = useSelector((_store: IAppReduxStore) => _store.projectSelectedDocuments.storage.documentIds);

    const openSupportPage = async () => {
        let universalIds: string[] = [];
        let docNumbers: string[] = [];
               
        if (selectedDocs.length) {
            const documents = findProjectDocuments(selectedDocs.slice(0, 50), areaName);
            
            universalIds = documents.map(doc => doc.UniversalID);

            // TechDoc_260401-8807-18
            // send DocNumbers instead of UniversalIds
            docNumbers = documents.map(doc => {
                if (doc.DocNumber && doc.DocIndex) {
                    return doc.DocNumber + ' - ' + doc.DocIndex;
                }
                
                if (doc.DocNumber) {
                    return doc.DocNumber;
                }
                
                if (doc.Subject) {
                    return doc.Subject.length > 30 ? (doc.Subject.substring(0, 30) + '...') : doc.Subject;
                }
                
                return doc.UniversalID;
            });
        }

        const url = new URL('https://thinkproject.my.site.com/support/s/contactsupport');
        url.searchParams.append('language', i18n.language);
        url.searchParams.append('InfraEmail', userProfile.email);
        url.searchParams.append('InfraUsername', userProfile.name);
        url.searchParams.append('InfraDatabase', projectData[replicaId]?.Title ? `${projectData[replicaId].Title}<{ActionLogNumber}:WC>` : '');
        url.searchParams.append('InfraDocId', docNumbers.join(','));

        const response = await store.dispatch(actionLogSupportTicket({ replicaId, universalIds, supportTicketUrl: url.toString().toString() }));
        
        if (response.meta.requestStatus === 'fulfilled') {
            const actionLogNumber = response.payload as string;
            url.searchParams.set('InfraDatabase', projectData[replicaId]?.Title ? `${projectData[replicaId].Title}<${actionLogNumber}:WC>` : '');
        }

        window.open(url.toString());
    };

    const options = useMemo(
        () => ({
            about: {
                component: 'link',
                link: {
                    en: 'https://app.storylane.io/share/zyulgruj3dbj',
                    de: 'https://app.storylane.io/share/zyulgruj3dbj',
                },
                icon: mdiInformation,
            },
            thinkprojectAcademy: {
                component: 'link',
                link: {
                    en: 'https://customer.academy.thinkproject.com/',
                    de: 'https://customer.academy.thinkproject.com/',
                },
                icon: mdiSchool,
            },
            helpCenter: {
                component: 'link',
                link: {
                    en: 'https://help.thinkproject.com/public/en/Content/LandingPages/CDI.htm',
                    de: 'https://help.thinkproject.com/public/de/Content/LandingPages/CDI.htm',
                },
                icon: mdiHelpCircle,
            },
            versionNumber: {
                component: 'link',
                link: {
                    en: `https://help.thinkproject.com/public/en/Content/CDI/CDI%20Release%20Notes/Release%20Notes%20${versionNumber}.htm`,
                    de: `https://help.thinkproject.com/public/de/Content/CDI/CDI%20Release%20Notes/Release%20Notes%20${versionNumber}.htm`,
                },
                icon: mdiCounter,
            },
            securityBulletins: {
                component: 'link',
                link: {
                    en: 'https://security-bulletins.eplass.de/',
                    de: 'https://security-bulletins.eplass.de/',
                },
                icon: mdiSecurity,
            },
            teamViewer: {
                component: 'link',
                link: {
                    en: 'https://get.teamviewer.com/3qf5zqv',
                    de: 'https://get.teamviewer.com/3qf5zqv',
                },
                icon: mdiTeamviewer,
            },
            ...(replicaId
                ? {
                      projectDocumentation: {
                          component: 'btn',
                          icon: mdiFileDocumentMultiple,
                          callback: () => {
                              dispatch(openProjectDocumentationDialog());
                          },
                      },
                      createSupportTicket: {
                          component: 'btn',
                          icon: mdiHelpRhombusOutline,
                          callback: () => {
                            openSupportPage();
                          },
                      },
                  }
                : {}),
        }),
        [replicaId],
    );

    return options ? (
        <Stack direction='column'>
            {Object.keys(options).map((key: string) => {
                return options[key].component === 'link' ? (
                    <MenuItem
                        sx={{ fontFamily: labelFontSemiBold }}
                        component={Link}
                        href={options[key].link[i18n.language] ?? options[key].link.en}
                        key={key}
                        underline='none'
                        target='_blank'
                    >
                        <Icon path={options[key].icon} size={1} />
                        <Box sx={{ ml: 1.5 }}>
                            {key === 'versionNumber' ? t(key, { version: versionNumber }) : t(key)}
                        </Box>
                    </MenuItem>
                ) : (
                    <MenuItem
                        sx={{ fontFamily: labelFontSemiBold }}
                        key={key}
                        onClick={options[key].callback}
                    >
                        <Icon path={options[key].icon} size={1} />
                        <Box sx={{ ml: 1.5 }}>{t(key)}</Box>
                    </MenuItem>
                );
            })}
        </Stack>
    ) : (
        <></>
    );
};
